import { useState } from 'react'

import type { Operation } from '@fit-editor/fit-core'

import { OperationPreviewSummary } from '@/components/edit/operation-preview-summary'
import { RangeFields } from '@/components/edit/range-fields'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useOperationPreview } from '@/hooks/use-operation-preview'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface CropDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type CropMode = 'before' | 'after' | 'range'

const MODE_LABELS: Record<CropMode, string> = {
  before: 'Keep everything before',
  after: 'Keep everything after',
  range: 'Keep only this range',
}

/**
 * Crop has its own dialog (rather than reusing `RangeOperationDialog`
 * directly) because, unique among this milestone's range operations, how
 * many boundaries it needs depends on the chosen mode: `before`/`after`
 * need exactly one, `range` needs both. It still reuses the same
 * `RangeFields` + `useOperationPreview` + `OperationPreviewSummary`
 * building blocks as every other range dialog.
 */
export function CropDialog({ open, onOpenChange }: CropDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const selectedRangeMs = useActivityStore((s) => s.selectedRangeMs)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)

  const [mode, setMode] = useState<CropMode>('range')
  const [start, setStart] = useState<Date | null>(null)
  const [end, setEnd] = useState<Date | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Prefill from the timeline selection during render (React's documented
  // "adjust state when a prop changes" pattern), not in a `useEffect` --
  // see `RangeOperationDialog`'s identical comment for why. `prevOpen`
  // starts at `false` (not `open`) so this also fires correctly if the
  // dialog is ever first rendered already-open.
  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open && start == null && end == null && selectedRangeMs != null) {
      setStart(new Date(selectedRangeMs.start))
      setEnd(new Date(selectedRangeMs.end))
    }
  }

  const needsStart = mode !== 'before'
  const needsEnd = mode !== 'after'
  const valid =
    (!needsStart || start != null) &&
    (!needsEnd || end != null) &&
    (mode !== 'range' || (start != null && end != null && start.getTime() < end.getTime()))

  const operation: Operation | null = valid
    ? {
        type: 'crop',
        mode,
        ...(needsStart && start != null ? { start } : {}),
        ...(needsEnd && end != null ? { end } : {}),
      }
    : null

  // Only fetches a baseline/preview while the dialog is actually open --
  // see `RangeOperationDialog`'s identical comment for why.
  const preview = useOperationPreview(open ? loaded?.history : undefined, operation)

  if (loaded == null) return null

  const handleConfirm = async () => {
    if (operation == null) return
    setSubmitting(true)
    try {
      // `null` means the apply failed (the store already surfaced a
      // toast) -- keep the dialog open rather than closing it as if the
      // crop had actually been applied.
      const warnings = await applyOperationToSelected(operation)
      if (warnings != null) onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="crop-description">
        <DialogTitle>Crop</DialogTitle>
        <DialogDescription id="crop-description">
          Permanently discards records and events outside the kept range. Laps and session
          boundaries are recalculated to stay consistent.
        </DialogDescription>

        <div className="flex flex-col gap-3">
          <div role="radiogroup" aria-label="Crop mode" className="flex flex-col gap-1">
            {(Object.keys(MODE_LABELS) as CropMode[]).map((m) => (
              <label key={m} className="text-foreground flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="crop-mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                />
                {MODE_LABELS[m]}
              </label>
            ))}
          </div>

          <RangeFields
            idPrefix="crop"
            showStart={needsStart}
            showEnd={needsEnd}
            startLabel={mode === 'after' ? 'Keep from' : 'Range start'}
            endLabel={mode === 'before' ? 'Keep until' : 'Range end'}
            start={start}
            end={end}
            onStartChange={setStart}
            onEndChange={setEnd}
            selectedRangeMs={selectedRangeMs}
          />

          <OperationPreviewSummary state={preview} ready={valid} />
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={!valid || submitting} onClick={() => void handleConfirm()}>
            Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
